// src/components/blog/BlogPostForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  createBlogPost,
  updateBlogPost,
  fetchBlogPost,
  fetchBlogCategories,
  deleteBlogPostDocument,
  fetchBlogTags,
  uploadFeaturedImage,
  clearBlogPostError,
  clearBlogPostSuccess,
} from '../../store/actions/blogPostActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import DocumentUploadModal from '../common/DocumentUploadModal';
import DocumentGallery from '../common/DocumentGallery';

const BlogPostForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedBlogPost, loading, error, success, categories, tags } = useSelector(
    (state) => state.blogPosts
  );
  const { users } = useSelector((state) => state.users || { users: [] });

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    status: 'DRAFT',
    isFeatured: false,
    allowComments: true,
    categoryId: '',
    tagIds: [],
    metaTitle: '',
    metaDescription: '',
    canonicalUrl: '',
    keywords: [],
    publishedAt: '',
    scheduledAt: '',
    readTime: '',
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [keywordInput, setKeywordInput] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  const isEditMode = !!id;

  const statusOptions = [
    { value: 'DRAFT', label: 'Brouillon' },
    { value: 'PENDING_REVIEW', label: 'En attente de relecture' },
    { value: 'PUBLISHED', label: 'Publié' },
    { value: 'SCHEDULED', label: 'Programmé' },
    { value: 'ARCHIVED', label: 'Archivé' },
  ];

  const tabs = [
    { id: 'content', label: 'Contenu' },
    { id: 'seo', label: 'SEO' },
    { id: 'settings', label: 'Paramètres' },
  ];

  // Quill editor modules
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['clean'],
    ],
  };

  // Styles object
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
    },
    tab: {
      padding: '1rem 1.5rem',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
      backgroundColor: '#ffffff',
    },
    content: {
      padding: '2rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    fullWidth: {
      gridColumn: 'span 2',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    editor: {
      height: '400px',
      marginBottom: '3rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    imageUpload: {
      marginTop: '1rem',
    },
    imagePreview: {
      width: '200px',
      height: '150px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
      backgroundColor: '#f3f4f6',
      border: '1px solid #e5e7eb',
      marginBottom: '1rem',
    },
    imageUploadButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    keywordsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
      padding: '0.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
    },
    keyword: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: '#e5e7eb',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
    },
    removeKeyword: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      fontSize: '0.875rem',
      padding: '0 0.25rem',
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    tag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tagSelected: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
      paddingTop: '2rem',
      borderTop: '1px solid #e5e7eb',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    slugPreview: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
  };

  useEffect(() => {
    dispatch(fetchBlogCategories({ limit: 100 }));
    dispatch(fetchBlogTags({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchBlogPost(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (selectedBlogPost) {
      setDocuments(selectedBlogPost.documents || []);
      setGalleryImages(selectedBlogPost.galleryImages || []);
    }
  }, [selectedBlogPost]);

  useEffect(() => {
    if (isEditMode && selectedBlogPost) {
      setFormData({
        title: selectedBlogPost.title || '',
        slug: selectedBlogPost.slug || '',
        excerpt: selectedBlogPost.excerpt || '',
        content: selectedBlogPost.content || '',
        status: selectedBlogPost.status || 'DRAFT',
        isFeatured: selectedBlogPost.isFeatured || false,
        allowComments: selectedBlogPost.allowComments ?? true,
        categoryId: selectedBlogPost.categoryId || '',
        tagIds: selectedBlogPost.tags?.map(t => t.id) || [],
        metaTitle: selectedBlogPost.metaTitle || '',
        metaDescription: selectedBlogPost.metaDescription || '',
        canonicalUrl: selectedBlogPost.canonicalUrl || '',
        keywords: selectedBlogPost.keywords || [],
        publishedAt: selectedBlogPost.publishedAt ? selectedBlogPost.publishedAt.split('T')[0] : '',
        scheduledAt: selectedBlogPost.scheduledAt ? selectedBlogPost.scheduledAt.split('T')[0] : '',
        readTime: selectedBlogPost.readTime || '',
      });
      if (selectedBlogPost.featuredImage) {
        setFeaturedImagePreview(selectedBlogPost.featuredImage.storagePath);
      }
    }
  }, [selectedBlogPost, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearBlogPostError());
      dispatch(clearBlogPostSuccess());
    };
  }, [dispatch]);

  // Generate slug from title
  useEffect(() => {
    if (!isEditMode && formData.title && !formData.slug) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, isEditMode]);

  // Calculate read time
  useEffect(() => {
    if (formData.content) {
      const words = formData.content.replace(/<[^>]*>/g, '').split(/\s+/).length;
      const readTime = Math.ceil(words / 200); // 200 words per minute
      setFormData(prev => ({ ...prev, readTime }));
    }
  }, [formData.content]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Le slug est requis';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Le slug ne peut contenir que des lettres minuscules, des chiffres et des tirets';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Le contenu est requis';
    }

    if (formData.metaDescription && formData.metaDescription.length > 160) {
      newErrors.metaDescription = 'La meta description ne doit pas dépasser 160 caractères';
    }

    if (formData.scheduledAt && formData.status === 'SCHEDULED') {
      const scheduledDate = new Date(formData.scheduledAt);
      const now = new Date();
      if (scheduledDate <= now) {
        newErrors.scheduledAt = 'La date programmée doit être dans le futur';
      }
    }

    return newErrors;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }, [errors]);

  const handleEditorChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: undefined }));
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()],
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword),
    }));
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tagIds: prev.tagIds.includes(tagId)
        ? prev.tagIds.filter(id => id !== tagId)
        : [...prev.tagIds, tagId],
    }));
  };

  const handleDocumentUploadComplete = (uploadedDocs) => {
    setDocuments(prev => [...prev, ...uploadedDocs]);
    setGalleryImages(prev => [...prev, ...uploadedDocs.filter(doc => doc.fileType?.startsWith('image/'))]);
  };

  const handleDocumentDelete = async (doc) => {
    try {
      await dispatch(deleteBlogPostDocument(id, doc.id));
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      setGalleryImages(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFeaturedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setFeaturedImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    if (isEditMode && id) {
      setUploadingImage(true);
      try {
        await dispatch(uploadFeaturedImage(id, file));
      } catch (error) {
        console.error('Image upload failed:', error);
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Find first tab with error
      if (validationErrors.title || validationErrors.slug || validationErrors.content) {
        setActiveTab('content');
      } else if (validationErrors.metaTitle || validationErrors.metaDescription) {
        setActiveTab('seo');
      } else {
        setActiveTab('settings');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const postData = {
        ...formData,
        // Convert empty strings to null for dates
        publishedAt: formData.publishedAt || null,
        scheduledAt: formData.scheduledAt || null,
      };

      if (isEditMode) {
        await dispatch(updateBlogPost(id, postData));
        // Upload featured image if changed and in edit mode
        if (featuredImage) {
          await dispatch(uploadFeaturedImage(id, featuredImage));
        }
      } else {
        const newPost = await dispatch(createBlogPost(postData));
        // Upload featured image if provided
        if (featuredImage && newPost.id) {
          await dispatch(uploadFeaturedImage(newPost.id, featuredImage));
        }
      }
      navigate('/admin/blog-posts');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm, featuredImage]);

  const handleCancel = useCallback(() => {
    const hasChanges = Object.keys(formData).some(key => {
      if (key === 'tagIds') return formData[key].length > 0;
      if (key === 'keywords') return formData[key].length > 0;
      return formData[key] !== '' && formData[key] !== false;
    }) || featuredImagePreview;

    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/blog');
    }
  }, [formData, featuredImagePreview, navigate]);

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? "Modifier l'article" : 'Créer un nouvel article'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearBlogPostError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearBlogPostSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Tabs */}
        <div style={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={styles.content}>
          {/* Content Tab */}
          <div style={activeTab === 'content' ? {} : { display: 'none' }}>
            <div style={styles.grid}>
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="title">
                    Titre *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ...(errors.title ? styles.inputError : {}),
                    }}
                    placeholder="Titre de l'article"
                  />
                  {errors.title && (
                    <div style={styles.errorText}>{errors.title}</div>
                  )}
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="slug">
                    Slug *
                  </label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ...(errors.slug ? styles.inputError : {}),
                    }}
                    placeholder="titre-de-l-article"
                  />
                  {errors.slug && (
                    <div style={styles.errorText}>{errors.slug}</div>
                  )}
                  <div style={styles.slugPreview}>
                    URL: /blog/{formData.slug || 'titre-de-l-article'}
                  </div>
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="excerpt">
                    Extrait
                  </label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    style={styles.textarea}
                    placeholder="Court résumé de l'article (optionnel)"
                    rows="3"
                  />
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="content">
                    Contenu *
                  </label>
                  <div style={styles.editor}>
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={handleEditorChange}
                      modules={quillModules}
                      placeholder="Rédigez votre article ici..."
                    />
                  </div>
                  {errors.content && (
                    <div style={styles.errorText}>{errors.content}</div>
                  )}
                </div>
              </div>
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <label style={styles.label}>Documents et images</label>
                    <button
                      type="button"
                      onClick={() => setShowDocumentModal(true)}
                      style={styles.imageUploadButton}
                    >
                      <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Ajouter des documents
                    </button>
                  </div>

                  <DocumentGallery
                    documents={documents}
                    entityId={id}
                    entityType="blogPost"
                    onDelete={handleDocumentDelete}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SEO Tab */}
          <div style={activeTab === 'seo' ? {} : { display: 'none' }}>
            <div style={styles.grid}>
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="metaTitle">
                    Meta titre
                  </label>
                  <input
                    type="text"
                    id="metaTitle"
                    name="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Titre pour les moteurs de recherche"
                  />
                  <div style={styles.helperText}>
                    Laissez vide pour utiliser le titre de l'article
                  </div>
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="metaDescription">
                    Meta description
                  </label>
                  <textarea
                    id="metaDescription"
                    name="metaDescription"
                    value={formData.metaDescription}
                    onChange={handleChange}
                    style={{
                      ...styles.textarea,
                      ...(errors.metaDescription ? styles.inputError : {}),
                    }}
                    placeholder="Description pour les moteurs de recherche"
                    rows="3"
                  />
                  {errors.metaDescription && (
                    <div style={styles.errorText}>{errors.metaDescription}</div>
                  )}
                  <div style={styles.helperText}>
                    {formData.metaDescription?.length || 0}/160 caractères
                  </div>
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="canonicalUrl">
                    URL canonique
                  </label>
                  <input
                    type="url"
                    id="canonicalUrl"
                    name="canonicalUrl"
                    value={formData.canonicalUrl}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="https://example.com/article"
                  />
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Mots-clés</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                      style={{ ...styles.input, flex: 1 }}
                      placeholder="Ajouter un mot-clé"
                    />
                    <button
                      type="button"
                      onClick={handleAddKeyword}
                      style={styles.imageUploadButton}
                    >
                      Ajouter
                    </button>
                  </div>
                  {formData.keywords.length > 0 && (
                    <div style={styles.keywordsContainer}>
                      {formData.keywords.map((keyword, index) => (
                        <span key={index} style={styles.keyword}>
                          {keyword}
                          <button
                            type="button"
                            onClick={() => handleRemoveKeyword(keyword)}
                            style={styles.removeKeyword}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Settings Tab */}
          <div style={activeTab === 'settings' ? {} : { display: 'none' }}>
            <div style={styles.grid}>
              {/* Featured Image */}
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Image à la une</label>
                  {featuredImagePreview && (
                    <img
                      src={featuredImagePreview}
                      alt="Featured"
                      style={styles.imagePreview}
                    />
                  )}
                  <div style={styles.imageUpload}>
                    <input
                      type="file"
                      id="featuredImage"
                      accept="image/*"
                      onChange={handleFeaturedImageUpload}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById('featuredImage').click()}
                      style={styles.imageUploadButton}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        'Téléchargement...'
                      ) : (
                        <>
                          <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {featuredImagePreview ? "Changer l'image" : 'Ajouter une image'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Status and Dates */}
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="status">
                  Statut
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="publishedAt">
                  Date de publication
                </label>
                <input
                  type="date"
                  id="publishedAt"
                  name="publishedAt"
                  value={formData.publishedAt}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>

              {formData.status === 'SCHEDULED' && (
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="scheduledAt">
                    Date programmée *
                  </label>
                  <input
                    type="datetime-local"
                    id="scheduledAt"
                    name="scheduledAt"
                    value={formData.scheduledAt}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ...(errors.scheduledAt ? styles.inputError : {}),
                    }}
                  />
                  {errors.scheduledAt && (
                    <div style={styles.errorText}>{errors.scheduledAt}</div>
                  )}
                </div>
              )}

              {/* Category */}
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="categoryId">
                  Catégorie
                </label>
                <select
                  id="categoryId"
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Tags</label>
                  <div style={styles.tagsContainer}>
                    {tags.map(tag => (
                      <span
                        key={tag.id}
                        style={{
                          ...styles.tag,
                          ...(formData.tagIds.includes(tag.id) ? styles.tagSelected : {}),
                        }}
                        onClick={() => handleTagToggle(tag.id)}
                      >
                        {tag.name}
                        {formData.tagIds.includes(tag.id) && ' ✓'}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Options */}
              <div style={styles.fieldGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleChange}
                    style={styles.checkbox}
                  />
                  Article à la une
                </label>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="allowComments"
                    checked={formData.allowComments}
                    onChange={handleChange}
                    style={styles.checkbox}
                  />
                  Autoriser les commentaires
                </label>
              </div>

              {/* Read Time (auto-calculated) */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Temps de lecture</label>
                <input
                  type="text"
                  value={`${formData.readTime || 0} minutes`}
                  disabled
                  style={{ ...styles.input, backgroundColor: '#f9fafb' }}
                />
                <div style={styles.helperText}>
                  Calculé automatiquement en fonction du contenu
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div style={styles.buttonGroup}>
            <button
              type="button"
              onClick={handleCancel}
              style={styles.cancelButton}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                ...styles.submitButton,
                ...(isSubmitting ? styles.submitButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
                }
              }}
            >
              {isSubmitting
                ? (isEditMode ? 'Modification...' : 'Création...')
                : (isEditMode ? 'Mettre à jour' : 'Publier')}
            </button>
          </div>
        </div>
      </form>

      {showDocumentModal && (
        <DocumentUploadModal
          entityId={id}
          entityType="blogPost"
          onClose={() => setShowDocumentModal(false)}
          onUploadComplete={handleDocumentUploadComplete}
          multiple={true}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          maxSize={10 * 1024 * 1024} // 10MB
        />
      )}
      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/blog-posts')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default BlogPostForm;